'use client'

import { useState } from 'react'

/**
 * Public /tools/* submit handler for the 3 pay-first tools (aeo-page-auditor,
 * ai-citation-tracker, brand-disambiguation).
 *
 * Calls the tool's route FIRST. Privileged callers — the signed-in owner or any
 * paid user (resolvePrivilegedToolAccess on the route) — get the FULL result,
 * rendered inline, free, no checkout. Everyone else gets a 402 paid_required
 * (the route executed NOTHING), so the page shows the $5 wall and the buyer
 * pays. Nothing runs before payment for a non-privileged visitor.
 *
 * The page reacts to the returned shape: `result` → render inline; `walled` →
 * show the $5 wall; `error` → show the message. `T` is the tool's result type.
 */
export function useToolRunOrCheckout<T>(endpoint: string) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<T | null>(null)
  const [walled, setWalled] = useState(false)

  async function run(input: Record<string, unknown>) {
    setLoading(true)
    setError(null)
    setResult(null)
    setWalled(false)
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      })

      // 402 = non-privileged → pay-first. The route ran nothing; show the wall.
      if (res.status === 402) {
        setWalled(true)
        return
      }

      const data = await res.json().catch(() => null)
      if (!res.ok) {
        setError(
          (data && typeof data.message === 'string' && data.message) ||
            'This run failed. Please try again.'
        )
        return
      }
      setResult(data as T)
    } catch {
      setError('This run failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  function reset() {
    setError(null)
    setResult(null)
    setWalled(false)
  }

  return { run, loading, error, result, walled, reset }
}
